// Mulberry32 — tiny seedable PRNG. Used by the lorem generator so
// tests can pass `seed` and get reproducible paragraph picks.
// https://gist.github.com/tommyettinger/46a3a48b7f1d49774f1bd05ed27f8418

export interface PRNG {
	/** Returns a float in [0, 1). */
	next: () => number;
	/** Returns an integer in [min, max] inclusive. */
	int: (min: number, max: number) => number;
	/** Picks a random element from the given array. */
	pick: <T>(arr: readonly T[]) => T;
}

export function mulberry32(seed: number): PRNG {
	let s = seed | 0;
	const next = () => {
		s = (s + 0x6d2b79f5) | 0;
		let t = s;
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
	const int = (min: number, max: number) =>
		min + Math.floor(next() * (max - min + 1));
	const pick = <T>(arr: readonly T[]): T => {
		if (arr.length === 0) {
			throw new Error("mulberry32.pick: cannot pick from empty array");
		}
		return arr[int(0, arr.length - 1)];
	};
	return { next, int, pick };
}

/**
 * Default PRNG when no seed is provided — wraps Math.random so calls
 * still go through the same `pick` / `int` interface.
 */
export function defaultPRNG(): PRNG {
	const next = () => Math.random();
	const int = (min: number, max: number) =>
		min + Math.floor(next() * (max - min + 1));
	const pick = <T>(arr: readonly T[]): T => {
		if (arr.length === 0) {
			throw new Error("defaultPRNG.pick: cannot pick from empty array");
		}
		return arr[int(0, arr.length - 1)];
	};
	return { next, int, pick };
}
